import React, { useEffect } from "react"
import { map } from "lodash"
import { formatPhoneNumber } from "../support/helpers"
import { IActivatable, ISharedContext, requiresQualification } from "../support/registration_form_component"
import { FormHelpers } from "../support/form_helpers"
import { ClaimValueTable } from "../support/claim_value_form"
import * as data from "../data"
import moment from "moment"
import { utcDateToUSDateString } from "@/shared/util/format"
import { ContactTypeName, getContactTypes, IContactType, InsurerType } from "../clients/config";
import { FormStatus } from "../wizard"
import { IClientConfig, ILicenseType, OTHER, TPAUse, PBMUse, CarveOutPayerUse, InsurerTypeName } from "../clients/config"

export type RegistrationReviewSubmitProps = IActivatable & ISharedContext & {
  suppress_attachment_download_warning?: boolean
}
export function RegistrationReviewSubmit(props: RegistrationReviewSubmitProps) {
  if (!props.active) {
    return <div />
  }

  return (
    <div className="step">
      <ReviewHeader status={props.status} />
      <ReviewSections
        config={props.config}
        client={props.client}
        form={props.form || {}}
        attachment={props.attachment}
        suppress_attachment_download_warning={props.suppress_attachment_download_warning}
      />
    </div>
  )
}

export interface IReviewHeaderProps {
  status: FormStatus
}
export function ReviewHeader(props: IReviewHeaderProps) {
  if (props.status === FormStatus.Complete) {
    return (
      <div>
        <h1>This form has been submitted to Onpoint successfully.</h1>

        <div className="alert alert-warning" role="alert">
          You may still review and print the form using the button in the bottom-right corner,
          but you may not make any changes. You may start a new registration using the
          &ldquo;Start Over&rdquo; button in the bottom-left corner.
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1>You are almost done! Let&apos;s review your information.</h1>

      <div className="alert alert-warning" role="alert">
        Please review your completed registration form. To make changes, use the &ldquo;Previous Step&rdquo; button or
        individual section links located at the bottom of the page. When ready, use the &ldquo;Finish &amp; Submit
        Form&rdquo; button located in the lower right-hand corner of the page to finalize your form for
        Onpoint&apos;s review.
      </div>
    </div>
  )
}

export interface IReviewSectionsProps {
  config: IClientConfig
  client: data.IClient
  form: data.IRegistrationForm
  attachment: data.IAttachment | null
  suppress_attachment_download_warning?: boolean
  print_on_load?: boolean
}
export function ReviewSections(props: IReviewSectionsProps) {
  useEffect(() => {
    if (props.print_on_load) {
      setTimeout(() => {
        window.print();
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run once on mount
  }, []);


  const config = props.config
  const client = props.client
  const form = props.form
  const eligibility = form.eligibility || {}

  const reporting = requiresQualification(client, form)
    ? FormHelpers.str2bool(eligibility.reporting || false)
    : true

  return (
    <div id="review-area">
      <ContactReviewPanel {...form} typeName="filler" contactType={config.fillerConfig} />

      <ReviewInsurer insurer={form?.insurer || {}} client_config={config} tpa_self_insured={form?.tpa_self_insured} />

      {form.certification && <MDCertification {...form} />}

      {map(FormHelpers.coverageTypes(), (coverageType) => {
        if (typeof form[coverageType.field] === "undefined") {
          return null
        }

        return <ClaimValues key={coverageType.field} coverageType={coverageType} {...props} />
      })}

      <Qualifier
        client={client}
        config={config}
        eligibility={form.eligibility}
        form={form}
      />

      {reporting ? (
        <>
          {getContactTypes(config, form?.insurer?.type).map(([typeName, contactType]) => {
            return (
              <ContactReviewPanel
                {...form}
                key={typeName}
                typeName={typeName}
                contactType={contactType}
              />
            )
          })}

          {client.has_tpa && (
            <ReviewTpa
              config={config}
              tpa={form?.tpa}
              insurer_type={form?.insurer?.type}
              tpa_self_insured={form?.tpa_self_insured}
            />
          )}
          {client.has_pbm && (
            <ReviewPbm
              config={config}
              pbm={form?.pbm}
              insurer_type={form?.insurer?.type}
            />
          )}
          {client.has_carve_out_payer && (
            <ReviewCarveOutPayer
              config={config}
              carve_out_payer={form?.carve_out_payer}
              insurer_type={form?.insurer?.type}
            />
          )}

          {client.has_schedule && form.submission_schedule && form.submission_schedule.frequency && <SubmissionSchedule {...form} /> }
          <ReviewComments comments={form.additional_contacts?.text || ""} />
          {client.has_registration_attachment && (
            <ReviewAttachment
              label={client.registration_attachment_label || "Attachment"}
              attachment={props.attachment}
              suppress_download_warning={props.suppress_attachment_download_warning}
            />
          )}
        </>
      ) : (
        <ReviewComments comments={form.additional_contacts?.text || ""} />
      )}
    </div>
  )
}

function MDCertification(props: data.IRegistrationForm) {
  const certification = props.certification

  if (!certification) {
    return <div />
  }

  const datetime = certification.datetime
  let formattedDatetime = ""
  if (datetime) {
    const m = moment(datetime)
    formattedDatetime = m.format("LLL")
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">Payor Certification of Encrypted Patient Identifiers, Encrypted Internal Subscriber Numbers, and Encrypted Contract Numbers</h1>
      </div>

      <table className="table table-condensed table-hover">
        <tbody>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Certifier Name</td>
            <td>{certification.certifier_name || null}</td>
          </tr>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Certifier Signature</td>
            <td>{certification.certifier_signature || null}</td>
          </tr>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Certifier Job Title</td>
            <td>{certification.certifier_job_title || null}</td>
          </tr>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Certifier Current Phone Number</td>
            <td>{formatPhoneNumber(certification.certifier_current_phone_number) || certification.certifier_current_phone_number || null}</td>
          </tr>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Certifier Current Email Address</td>
            <td>{certification.certifier_current_email_address || null}</td>
          </tr>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Certification Date and Time</td>
            <td>{formattedDatetime}</td>
          </tr>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Certified</td>
            <td>{FormHelpers.str2bool(certification.certified) ? "Yes" : "No"}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

interface IContactReviewPanelProps extends data.IRegistrationForm {
  typeName: ContactTypeName
  contactType: IContactType
}

function ContactReviewPanel(props: IContactReviewPanelProps) {
  const typeName = props.typeName
  const contactRef = props[typeName] as data.IContactRef
  if (!contactRef) {
    return null
  }

  const title = props.contactType.title

  const contactUUID = contactRef.contact
  if (contactUUID === data.NOT_APPLICABLE) {
    return (
      <div className="panel panel-default">
        <div className="panel-heading">
          <h1 className="panel-title">{title}</h1>
        </div>
        <table className="table table-condensed table-hover">
          <tbody>
            <tr>
              <td colSpan={2} className="data-name col-sm-3 col-lg-4">Not Applicable</td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  let contactInfo: data.IContact | undefined

  if (contactUUID) {
    const contacts = props.contacts || {}
    contactInfo = contacts[contactUUID]
  }

  if (typeof contactInfo === "undefined") {
    contactInfo = {}
  }

  const plans = props.plans || ({} as Record<string, data.IPlanParentOrganization>)

  let notificationOptIn = true
  if (typeof contactInfo.notification_opt_in === "string") {
    notificationOptIn = contactInfo.notification_opt_in === data.YES
  }

  // let address
  // if (contactInfo) {
  //   address = reject([
  //     contactInfo.street_1,
  //     contactInfo.street_2,
  //     contactInfo.city,
  //     contactInfo.state,
  //     contactInfo.zip,
  //   ], (el) => { return !el || !el.length || el.length === 0 }).join(", ")
  // }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">{title}</h1>
      </div>

      <table className="table table-condensed table-hover">
        {contactInfo ? (
          <tbody>
            {contactRef.plan_codes ? (
              <tr>
                <td className="data-name col-sm-3 col-lg-4">Plans</td>
                <td>
                  {contactRef.plan_codes.map((selectedPlanCode, index) => {
                    // We have already asserted that the Array exists, so this type coercion is safe.
                    const isLast = index === (contactRef.plan_codes as data.ISelectedPlanCode[]).length - 1

                    if ( !selectedPlanCode.code ) {
                      return null
                    }

                    if ( selectedPlanCode.code === data.OTHER ) {
                      return (
                        <p key={index} className={isLast ? "no-margin" : ""}>
                          <strong>I don&apos;t see my plan:</strong> &ldquo;{selectedPlanCode.other_name}&rdquo;
                        </p>
                      )
                    }
                    else if ( selectedPlanCode.code === data.NOT_APPLICABLE ) {
                      return (
                        <p key={index} className={isLast ? "no-margin" : ""}>
                            Not Applicable
                        </p>
                      )
                    } else {
                      const plan = plans[ selectedPlanCode.code ]

                      return (
                        <p key={index} className={isLast ? "no-margin" : ""}>
                          {(plan && plan.planName) || ""} ({selectedPlanCode.code || "No Plan Code Available"})
                        </p>
                      )
                    }
                  }).filter(o => !!o)}
                </td>
              </tr>
            ) : null}
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Name</td>
              <td>
                {contactInfo.first_name || ""} {contactInfo.last_name || ""}
              </td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Email</td>
              <td>{contactInfo.email || null}</td>
            </tr>
            {contactInfo["phone"] ? (
              <tr>
                <td className="data-name">Phone</td>
                <td>
                  {formatPhoneNumber(contactInfo.phone) || contactInfo.phone || null}
                  {contactInfo.phone_extension && (
                    <span>
                        &nbsp;
                      {`x${contactInfo.phone_extension}`}
                    </span>
                  )}
                </td>
              </tr>
            ) : null}
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Organization Name</td>
              <td>{contactInfo.company_name}</td>
            </tr>
            {contactInfo.title ? (
              <tr>
                <td className="data-name col-sm-3 col-lg-4">Job Title</td>
                <td>{contactInfo.title}</td>
              </tr>
            ) : null}
            {contactInfo.street_1 ? (
              <tr>
                <td className="data-name col-sm-3 col-lg-4">Mailing Address</td>
                <td>{contactInfo.street_1}</td>
              </tr>
            ) : null}
            {contactInfo.street_2 ? (
              <tr>
                <td className="data-name col-sm-3 col-lg-4">Mailing Address (Line 2)</td>
                <td>{contactInfo.street_2}</td>
              </tr>
            ) : null}
            <tr>
              <td className="data-name col-sm-3 col-lg-4">City</td>
              <td>{contactInfo.city || null}</td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">State</td>
              <td>{contactInfo.state || null}</td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">ZIP Code</td>
              <td>{contactInfo.zip || null}</td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Email Notifications</td>
              <td>{notificationOptIn ? "Yes" : "No"}</td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Reporting</td>
              <td>{contactUUID === data.NOT_APPLICABLE ? "Not Applicable" : "Not Reported"}</td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  )
}

interface ReviewInsurerProps {
  insurer: data.IInsurer
  client_config: IClientConfig
  tpa_self_insured?: data.ITpaSelfInsured
}
function ReviewInsurer(props: ReviewInsurerProps) {
  const client_config = props.client_config
  const insurer = props.insurer

  const naics = insurer.naics || []
  const feins = insurer.feins || []

  const licenseTypeFieldLabel = client_config.licenseTypeFieldLabel ?? "License Type"
  const showLicenseTypes = (client_config.licenseTypes?.length || 0) > 0
  const showLicenseTypeNumbers = (insurer.license_types?.length || 0) > 1

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">Organization Information</h1>
      </div>

      <table className="table table-condensed table-hover">
        <tbody>
          {client_config.hasParentCompanyName && (
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Parent Company Name</td>
              {insurer.parent_company_name === data.OTHER ? (
                <td><strong>I don&apos;t see my parent company:</strong> {insurer.custom_parent_company_name}</td>
              ) : (
                <td>{insurer.parent_company_name}</td>
              )}
            </tr>
          )}
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Submitter Name</td>
            <td>{insurer.company_name}</td>
          </tr>
          {typeof insurer.payer_name === "string" ? (
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Submitter Parent Name</td>
              <td>{insurer.payer_name}</td>
            </tr>
          ) : null}
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Submitter Code</td>
            <td>{insurer.submitter_code && <span>{insurer.submitter_code}</span>}</td>
          </tr>
          {client_config.showSubmitterType ? (<tr>
            <td className="data-name col-sm-3 col-lg-4">Submitter Type</td>
            <td>{insurer.type && <span>{insurer.type}</span>}</td>
          </tr>) : null}
          {client_config.showParticipantType ? (<tr>
            <td className="data-name col-sm-3 col-lg-4">Type of Participant</td>
            <td>{insurer.participant_type && <span>{insurer.participant_type}</span>}</td>
          </tr>) : null}
          <tr>
            <td className="data-name col-sm-3 col-lg-4">NAIC</td>
            <td>
              {naics.map((item, i) => {
                return <div key={i}>{item}</div>
              })}
            </td>
          </tr>
          {client_config.hasFein && (
            <tr>
              <td className="data-name col-sm-3 col-lg-4">FEIN</td>
              <td>
                {feins.map((item, i) => {
                  return <div key={i}>{item}</div>
                })}
              </td>
            </tr>
          )}
          {showLicenseTypes ? (insurer.license_types || [{}]).map((value, index) => {
            const licenseTypeNumber = showLicenseTypeNumbers ? index + 1 : null

            if (!value.name) {
              return (
                <tr key={`license_type_${index}`}>
                  <td className="data-name col-sm-3 col-lg-4">{licenseTypeFieldLabel} {licenseTypeNumber}</td>
                  <td></td>
                </tr>
              )
            }

            const licenceType: ILicenseType = client_config.licenseTypes?.find((type) => type.name === value.name) || {
              name: "other",
              label: `Unknown License Type: ${value.name}`,
              require_custom_name: false,
              require_license_number: false,
              show_license_number: true,
            }

            return (<React.Fragment key={`license_type_${index}`}>
              <tr>
                <td className="data-name col-sm-3 col-lg-4">{licenseTypeFieldLabel} {licenseTypeNumber}</td>
                <td>{
                  licenceType.require_custom_name
                    ? `${licenceType.label}: ${value.custom_name || ""}`
                    : licenceType.label
                }</td>
              </tr>
              {client_config.showLicensedEntityName && (
                <tr>
                  <td className="data-name col-sm-3 col-lg-4" style={{paddingLeft: "5.0rem"}}>Licensed Entity Name {licenseTypeNumber}</td>
                  <td>{value.licensed_entity_name}</td>
                </tr>
              )}
              {licenceType.show_license_number && (
                <tr>
                  <td className="data-name col-sm-3 col-lg-4" style={{paddingLeft: "5.0rem"}}>License Number {licenseTypeNumber}</td>
                  <td>{value.license_number}</td>
                </tr>
              )}
            </React.Fragment>)
          }) : null}
          {client_config.marketCategories ? (
            (insurer.market_categories ?? []).map((market_category, i) => {
              const coveredLives: string = (insurer.market_category_covered_lives ?? [])[i] ?? "N/A"
              return (
                <React.Fragment key={`market_category_${i}`}>
                  <tr>
                    <td className="data-name col-sm-3 col-lg-4">Market Category {i + 1}</td>
                    <td>{market_category ?? ""}</td>
                  </tr>
                  <tr>
                    <td className="data-name col-sm-3 col-lg-4" style={{paddingLeft: "5.0rem"}}>Covered Lives</td>
                    <td>{coveredLives}</td>
                  </tr>
                </React.Fragment>
              )
            })
          ) : null}
          {insurer.street_1 ? (
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Mailing Address</td>
              <td>{insurer.street_1}</td>
            </tr>
          ) : null}
          {insurer.street_2 ? (
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Mailing Address (Line 2)</td>
              <td>{insurer.street_2}</td>
            </tr>
          ) : null}
          <tr>
            <td className="data-name col-sm-3 col-lg-4">City</td>
            <td>{insurer.city || null}</td>
          </tr>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">State</td>
            <td>{insurer.state || null}</td>
          </tr>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">ZIP Code</td>
            <td>{insurer.zip || null}</td>
          </tr>
          {client_config.showTpaSelfInsuredForm && insurer.type === InsurerType.ThirdPartyAdministrator && (
            <ReviewTpaSelfInsured tpa_self_insured={props.tpa_self_insured} />
          )}
        </tbody>
      </table>
    </div>
  )
}

interface IClaimValuesProps {
  form: data.IRegistrationForm
  coverageType: {
    field: keyof data.FormCoverageEstimates
    title: string
  }
}
function ClaimValues({
  form,
  coverageType,
}: IClaimValuesProps) {
  const value = form[coverageType.field]

  if (!data.HasCoverageEstimatesSections.has(form?.insurer?.type as InsurerTypeName)) {
    return null
  }

  //  Because the read-only version of claim values still needs the following props:
  const claimValueTableProps = ClaimValueTable.readOnlyProps()

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">
          <span className="required">{coverageType.title} Coverage Estimates</span>
        </h1>
      </div>

      {value ? (
        <ClaimValueTable
          key={coverageType.field}
          title={coverageType.title}
          field={coverageType.field}
          form={form}
          {...claimValueTableProps}
        />
      ) : (
        <table className="table table-condensed table-hover">
          <tbody>
            <tr>
              <td>Not Submitting Claims</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  )
}

interface ReviewTpaProps extends data.IRegistrationForm {
  config: IClientConfig
  insurer_type?: string
  tpa?: data.ITPA
  tpa_self_insured?: data.ITpaSelfInsured
}
function ReviewTpa({
  config,
  insurer_type,
  tpa,
  tpa_self_insured,
}: ReviewTpaProps) {
  if (!config.tpaUseQuestion || !data.HasCarveOutSections.has(insurer_type as InsurerTypeName) || !tpa) {
    return null
  }

  const showOther = tpa?.company_name === data.OTHER
  const selection = tpa?.["work-with"] ?? TPAUse.NO_TPA
  const selectionLabel = config.tpaUseQuestion?.options.find(option => option.value === selection)?.label ?? "Unknown Selection"

  const naics = tpa?.naics ?? []
  const feins = tpa?.feins ?? []

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">TPA</h1>
      </div>

      <table className="table table-condensed table-hover">
        {(data.SHOW_CLAIM_INFO_FOR.includes(selection)) ? (
          <tbody>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Selection</td>
              <td>{selectionLabel}</td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Organization</td>
              <td>{showOther
                ? `Other (Please Specify): ${tpa?.custom_company_name ?? ""}`
                : (tpa?.company_name ?? "")}</td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">NAIC</td>
              <td>
                {naics.map((item, i) => {
                  return <div key={i}>{item}</div>
                })}
              </td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">FEIN</td>
              <td>
                {feins.map((item, i) => {
                  return <div key={i}>{item}</div>
                })}
              </td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Submitting Organization</td>
              <td>{tpa?.organization ?? ""}</td>
            </tr>
            {config.showTpaSelfInsuredForm && selection === TPAUse.WE_ARE_THE_TPA && (
              <ReviewTpaSelfInsured tpa_self_insured={tpa_self_insured} />
            )}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Selection</td>
              <td>{selectionLabel}</td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  )
}

interface ReviewPbmProps extends data.IRegistrationForm {
  config: IClientConfig
  insurer_type?: string
  pbm?: data.IPBM
}
function ReviewPbm({
  config,
  insurer_type,
  pbm,
}: ReviewPbmProps) {
  if (!config.pbmUseQuestion || !data.HasCarveOutSections.has(insurer_type as InsurerTypeName) || !pbm) {
    return null
  }

  const showOther = pbm?.company_name === data.OTHER
  const selection = pbm?.["work-with"] ?? PBMUse.NO_PBM
  const selectionLabel = config.pbmUseQuestion?.options.find(option => option.value === selection)?.label ?? "Unknown Selection"

  const naics = pbm?.naics ?? []
  const feins = pbm?.feins ?? []

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">PBM</h1>
      </div>

      <table className="table table-condensed table-hover">
        {(data.SHOW_CLAIM_INFO_FOR.includes(selection)) ? (
          <tbody>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Selection</td>
              <td>{selectionLabel}</td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Organization</td>
              <td>{showOther
                ? `Other (Please Specify): ${pbm?.custom_company_name ?? ""}`
                : (pbm?.company_name ?? "")}</td>
            </tr>
            {config.showPbmRegistrationNumber && (
              <tr>
                <td className="data-name col-sm-3 col-lg-4">{config.pbmRegistrationNumberLabel ?? "PBM Registration Number"}</td>
                <td>{pbm?.pbm_registration_number ?? ""}</td>
              </tr>
            )}
            <tr>
              <td className="data-name col-sm-3 col-lg-4">NAIC</td>
              <td>
                {naics.map((item, i) => {
                  return <div key={i}>{item}</div>
                })}
              </td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">FEIN</td>
              <td>
                {feins.map((item, i) => {
                  return <div key={i}>{item}</div>
                })}
              </td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Submitting Organization</td>
              <td>{pbm?.organization ?? ""}</td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Selection</td>
              <td>{selectionLabel}</td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  )
}

interface ReviewCarveOutPayerProps extends data.IRegistrationForm {
  config: IClientConfig
  carve_out_payer?: data.ICarveOutPayer
  insurer_type?: string
}
function ReviewCarveOutPayer({
  config,
  carve_out_payer,
  insurer_type,
}: ReviewCarveOutPayerProps) {
  if (!config.carveOutPayerUseQuestion || !data.HasCarveOutSections.has(insurer_type as InsurerTypeName) || !carve_out_payer) {
    return null
  }

  const showOther = carve_out_payer?.company_name === data.OTHER
  const selection = carve_out_payer?.["work-with"] ?? CarveOutPayerUse.NO_CARVE_OUT_PAYER
  const selectionLabel = config.carveOutPayerUseQuestion?.options.find(option => option.value === selection)?.label ?? "Unknown Selection"

  const naics = carve_out_payer?.naics ?? []
  const feins = carve_out_payer?.feins ?? []

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">Behavioral Health Carve-Out Payer</h1>
      </div>

      <table className="table table-condensed table-hover">
        {(data.SHOW_CLAIM_INFO_FOR.includes(selection)) ? (
          <tbody>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Selection</td>
              <td>{selectionLabel}</td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Organization</td>
              <td>{showOther
                ? `Other (Please Specify): ${carve_out_payer?.custom_company_name ?? ""}`
                : (carve_out_payer?.company_name ?? "")}</td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">NAIC</td>
              <td>
                {naics.map((item, i) => {
                  return <div key={i}>{item}</div>
                })}
              </td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">FEIN</td>
              <td>
                {feins.map((item, i) => {
                  return <div key={i}>{item}</div>
                })}
              </td>
            </tr>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Submitting Organization</td>
              <td>{carve_out_payer?.organization ?? ""}</td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Selection</td>
              <td>{selectionLabel}</td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  )
}


interface ISubmissionScheduleProps {
  submission_schedule?: data.ISubmissionSchedule
}
function SubmissionSchedule({ submission_schedule }: ISubmissionScheduleProps) {
  const submissionSchedule = submission_schedule || { frequency: "" }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">Submission Schedule</h1>
      </div>

      <table className="table table-condensed table-hover">
        <tbody>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Schedule:</td>
            <td>{submissionSchedule.frequency || null}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

interface IReviewCommentsProps {
  comments: string
}
function ReviewComments({ comments } : IReviewCommentsProps) {
  if (!comments || !comments.length || comments.length === 0) {
    return null
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">Comments</h1>
      </div>

      <table className="table table-condensed table-hover">
        <tbody>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Comments:</td>
            <td>
              <div>{comments}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function Qualifier({
  client,
  config,
  eligibility,
  form,
}: {
  client: data.IClient
  config: IClientConfig
  eligibility?: data.IEligibility
  form: data.IRegistrationForm
}) {
  if (!requiresQualification(client, form)) {
    return null
  }

  const qualifies = FormHelpers.str2bool(eligibility?.reporting || false)

  // Don't need to show anything if registrant qualifies and client has no qualification date (the old default behavior before DO-562)
  if (qualifies && !config.showQualificationDateField) {
    return null
  }

  const reasonForNotReporting = eligibility?.reason_for_not_reporting

  const OTHER_DISPLAY_TEXT = "Other (please specify)"
  const reasonToShow = (reasonForNotReporting === OTHER ? OTHER_DISPLAY_TEXT : reasonForNotReporting) || null

  let qualificationDate: Date | null = null
  if (eligibility?.qualification_date) {
    qualificationDate = new Date(eligibility.qualification_date)
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">Qualifier</h1>
      </div>

      <table className="table table-condensed table-hover">
        <tbody>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">Qualifier:</td>
            <td>{qualifies ? "Eligible for Reporting" : "Not Eligible for Reporting"}</td>
          </tr>
          {qualifies && config.showQualificationDateField && (
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Qualification Date:</td>
              <td>{utcDateToUSDateString(qualificationDate)}</td>
            </tr>
          )}
          {!qualifies && config.qualifierReasons && config.qualifierReasons.length > 0 && (
            <tr>
              <td className="data-name col-sm-3 col-lg-4">Reason:</td>
              <td>{reasonToShow || null}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

interface IReviewAttachmentProps {
  label: string
  attachment: data.IAttachment | null
  suppress_download_warning?: boolean
}
function ReviewAttachment({
  label,
  attachment,
  suppress_download_warning = false,
}: IReviewAttachmentProps) {
  const fileType = attachment?.metadata?.analyzed_human_type
  const scary = !attachment?.safe

  const analysisResult = fileType ? `was determined to be '${fileType}'` : "could not be determined"

  const prompt = scary
    ? `WARNING: This file may not be safe to download! Its type ${analysisResult}. It should be handled with extreme caution. Are you sure you still want to download it?`
    : "This file may not be safe, and should be handled with caution. Are you sure you want to download it?"

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">{label}</h1>
      </div>

      <table className="table table-condensed table-hover">
        <tbody>
          <tr>
            <td className="data-name col-sm-3 col-lg-4">File name:</td>
            <td>
              {attachment && (<>
                <a
                  href={attachment.url}
                  onClick={(event) => {
                    if (suppress_download_warning) {
                      return
                    }

                    event.preventDefault()
                    if (window.confirm(prompt)) {
                      window.open(attachment.url, "_blank")
                    }
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download this file"
                >
                  {attachment.filename}
                </a>
                <span className={scary ? "text-danger" : ""} style={{paddingLeft: "1.0em"}}>({fileType || "Unknown"})</span>
              </>)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const TPA_SELF_INSURED_REVIEW_LABELS: Record<keyof data.ITpaSelfInsured, string> = {
  elected: "1. Self-insured Elected",
  declined: "2. Self-insured Declined",
  no_response: "3. Self-insured No Response",
}

interface IReviewTpaSelfInsuredProps {
  tpa_self_insured?: data.ITpaSelfInsured
}
function ReviewTpaSelfInsured({ tpa_self_insured }: IReviewTpaSelfInsuredProps) {
  return (
    <>
      {(Object.keys(TPA_SELF_INSURED_REVIEW_LABELS) as Array<keyof data.ITpaSelfInsured>).map((responseType) => {
        const question = tpa_self_insured?.[responseType]
        return (
          <tr key={`tpa-self-insured-${responseType}`}>
            <td className="data-name col-sm-3 col-lg-4">{TPA_SELF_INSURED_REVIEW_LABELS[responseType]}</td>
            <td>
              <div>{question?.any_clients === data.YES ? "Yes" : question?.any_clients === data.NO ? "No" : "Unknown"}</div>
              {question?.any_clients === data.YES && (
                <>
                  <div>Number of Groups: {question?.number_of_groups}</div>
                  <div>Covered Lives: {question?.covered_lives}</div>
                  <div>Claims Volume: {question?.claims_volume}</div>
                  <div>Aggregated Claim Value (USD): {question?.aggregated_claim_value}</div>
                </>
              )}
            </td>
          </tr>
        )
      })}
    </>
  )
}
